/*
 * Authors: mariushirjanu
 * Created: 09/08/2022
 */
import * as _ from 'underscore';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GlobalProductEditService } from './global-product-edit.service';
import { util } from '../../../shared/util';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { GlobalProductService } from '../global-product.service';
import { ScrollService } from '../../../shared/scroll/scroll.service';
import { IGlobalItem, IProperty, IPropertyGroup, IPropertyOption, IPropertyOptionValue } from '../global-product-data.service';
import { IDescribedNamedObject, INamedValue } from '../../../../type_definitions/datatypes';


export interface IFixedPropertyModel {
  field?:IFixedPropertyModel;
  choice:IPropertyOptionValue;
  name:string;
  value?:any;
  unit:string;
  properties:IProperty[];
}

@Component({
  selector: 'specs-new-property-dialog',
  template: `
    <div class="add-property-form">
      <div class="documents-dialog view-form">
        <div class="modal-header">
          <h4 class="modal-title">Add New Property</h4>
        </div>

        <div class="modal-body view-detail">

          <div class="add-fixed-form add-form clearfix" [hidden]="!addFixedPropertyFormShow">
            <form novalidate>
              <formly [form]="fixedPropertyForm" [fields]="fixedPropertyFields" [model]="fixedPropertyModel" class="form-horizontal"></formly>
              <formly [form]="fixedValueForm" [fields]="fixedValueFields" [model]="fixedValueModel" class="form-horizontal"></formly>
              <button class="btn btn-success float-end" (click)="addFixedProperty()">Add Predefined Property</button>
            </form>
          </div>
          <div class="add-free-form clearfix" [class]="{'add-form': addFixedPropertyFormShow}">
            <form novalidate>
              <formly [form]="freePropertyForm" [fields]="freePropertyFields" [model]="freePropertyModel" class="form-horizontal"></formly>
              <button class="btn btn-success float-end" (click)="addFreeProperty()">{{addFreePropertyButtonText}}</button>
              <a href class="float-end from-excel" (click)="addMultipleFreeProperties($event)">{{excelText}}</a>
            </form>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-danger" [disabled]="saving" (click)="closeDialog()">Don't Add</button>
        </div>
      </div>
    </div>
  `
})
export class SpecsNewPropertyDialogComponent implements OnInit {
  @Input() public model:IGlobalItem;
  @Output() public onSave:EventEmitter<IFixedPropertyModel|INamedValue[]> = new EventEmitter<IFixedPropertyModel>();
  @Output() public onClose:EventEmitter<void> = new EventEmitter<void>();

  public fixedPropertyForm:UntypedFormGroup = new UntypedFormGroup({});
  public fixedValueForm:UntypedFormGroup = new UntypedFormGroup({});
  public freePropertyForm:UntypedFormGroup = new UntypedFormGroup({});

  public fixedPropertyModel:IFixedPropertyModel = <any>{};
  public fixedValueModel:{value:any} = <any>{};
  public freePropertyModel = <any>{};

  public fixedPropertyFields:IExtraFieldConfigurationObject[] = [];
  public fixedValueFields:IExtraFieldConfigurationObject[] = [];
  public freePropertyFields:IExtraFieldConfigurationObject[] = [];

  public addFixedPropertyFormShow:boolean;
  public saving:boolean;
  public addFreePropertyButtonText:string;
  public excelText:string;
  private freePropertyKeyField:IExtraFieldConfigurationObject;
  private freePropertyValueField:IExtraFieldConfigurationObject;
  private multipleFreePropertiesField:IExtraFieldConfigurationObject;
  private fixedPropertyChoiceField:IExtraFieldConfigurationObject;
  private multiple:boolean;

  constructor(
    private formlyService:GaFormlyService,
    private globalProductEditService:GlobalProductEditService,
    private globalProductService:GlobalProductService,
    private messageService:GaMessageService,
    private scrollService:ScrollService,
    private validateService:GaValidateService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.start();
  }

  private start = ():void => {
    this.createFields();
    this.updateFreePropertyStatus();
    this.fillFields();
  };

  private createFields():void {
    if (!this.fixedPropertyFields.length) {
      this.fixedPropertyFields = this.createFixedFormFields();
    }

    if (!this.freePropertyFields.length) {
      this.freePropertyFields = this.createFreeFormFields();
    }
  }

  private createFixedFormFields():IExtraFieldConfigurationObject[] {
    const fixedPropertyTitle:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<h3>Add predefined properties</h3>')
      .build();

    this.fixedPropertyChoiceField = this.formlyService.aSelect('choice', 'Property')
      .isRequired()
      .onChange((choice:IPropertyOptionValue) => {
        this.fixedPropertyModel.field = <any>{};
        if (choice) {
          const propertyList:IPropertyGroup = _.findWhere(this.globalProductService.properties(), {'name': choice.list});
          const property:IProperty = _.findWhere(propertyList.values, {'name': choice.field});
          const field:IExtraFieldConfigurationObject = this.globalProductEditService.buildPropertyField(undefined, 'Value', property.control, property, this.fixedPropertyModel.field, true);

          // delete previous model value
          this.fixedValueModel = <any>{};
          this.fixedValueForm.markAsPristine();
          // add the new field to the form
          this.fixedValueFields = [field];
        }
      })
      .withLabelProp('description')
      .withValueProp('value')
      .withGroupProp('group')
      .build();

    return [
      fixedPropertyTitle,
      this.fixedPropertyChoiceField
    ];
  }

  private createFreeFormFields():IExtraFieldConfigurationObject[] {
    const freePropertyTitle:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<h3>Add free properties</h3>')
      .build();

    const freePropertyDisclaimer:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<p class="text-warning"><i class="fa fa-warning"></i> Disclaimer: Free properties are for informational purposes only. They cannot be used for reporting or filtering.</p>')
      .build();

    this.freePropertyKeyField = this.formlyService.anInput('name', 'Label')
      .isRequired()
      .withHideExpression(() => this.multiple)
      .withExtraOptions({
        validators: {
          checkDuplicates: {
            expression: (control:UntypedFormControl) => {
              const propertyFound:boolean = _.some(this.model.freeProperties, (freeProperty:INamedValue) => control?.value && control.value.toLowerCase() === freeProperty.name?.toLowerCase());
              return !propertyFound;
            },
            message: 'This property already exist, please use a different property label'
          }
        }
      })
      .build();

    this.freePropertyValueField = this.formlyService.anInput('value', 'Value')
      .isRequired()
      .withHideExpression(() => this.multiple)
      .build();

    this.multipleFreePropertiesField = this.formlyService.aTextarea('multiple', 'Free Properties')
      .withLabelClass('col-md-12')
      .withValueClass('col-md-12')
      .isHide()
      .withHideExpression(() => !this.multiple)
      .withPlaceholder('Paste excel data')
      .withExtraOptions({
        validators: {
          format: {
            expression: (control:UntypedFormControl) => {
              if (control.value) {
                const valueLines = control.value.split('\n');
                return _.every(valueLines, (line:string) => {
                  // each line should be 2 strings separated by 1 tab.
                  // Leading and trailing whitespace (per line) are ignored'
                  return /^\s*[^\t\r\n]+\t[^\t\r\n]+\s*$|^\s*$/.test(line);
                });
              }
            },
            message: 'The data is not correctly formatted. Each line should contain 2 values separated by one TAB character'
          },
          duplicates: {
            expression: (control:UntypedFormControl) => {
              const duplicateMessage:string[] = this.getFreePropertiesDuplicateErrors(control.value);
              return duplicateMessage?.length === 0;
            },
            message: () => {
              return `The following keys have to be unique: ${this.getFreePropertiesDuplicateErrors()?.join(', ')}`;
            }
          }
        }
      })
      .build();

    return [
      freePropertyTitle,
      freePropertyDisclaimer,
      this.freePropertyKeyField,
      this.freePropertyValueField,
      this.multipleFreePropertiesField
    ];
  }

  private fillFields():void {
    this.formlyService.fillField(this.fixedPropertyChoiceField, Promise.resolve(this.getFilteredFixedProperties())
      .then((properties:IPropertyGroup[]) => {
        const options:IPropertyOption[] = [];
        _.each(properties, (propertyList:IPropertyGroup) => {
          const propListModel:IProperty = _.findWhere(this.fixedPropertyModel.properties, { name: propertyList.name });
          _.each(propertyList.values, (property:IProperty) => {
            let propModel:IDescribedNamedObject;
            if (propListModel?.values) {
              propModel = _.findWhere(propListModel.values, { name: property.name });
            }
            if (!propModel) {
              const option:IPropertyOption = {
                description: property.name,
                value: {
                  list: propertyList.name,
                  field: property.name,
                  control: property.control,
                  definitionId: property.definitionId
                },
                group: propertyList.name
              };
              options.push(option);
            }
          });
        });

        this.addFixedPropertyFormShow = !!options.length;
        return options;
      }));
  }

  public addFixedProperty():void {
    const fixedModel:IFixedPropertyModel = util.copyAndExtend(this.fixedPropertyModel, this.fixedValueModel);
    this.validateProperties(this.fixedPropertyFields.concat(this.fixedValueFields), fixedModel)
      .then(() => {
        this.onSave.emit(fixedModel);
      });
  }

  public addFreeProperty():void {
    let fields:IExtraFieldConfigurationObject[];
    if (this.globalProductEditService.multipleFreeProperties()) {
      fields = [ this.multipleFreePropertiesField ];
    } else {
      fields = [ this.freePropertyKeyField, this.freePropertyValueField ];
    }

    this.validateProperties(fields, this.freePropertyModel)
      .then(() => {
        const freePropertyModel:INamedValue[] = this.globalProductEditService.multipleFreeProperties() ? this.formatMultipleFreeProperties() : this.formatIndividualFreeProperty();
        this.onSave.emit(freePropertyModel);
      });
  }

  private formatMultipleFreeProperties(unformattedProperties?:string):INamedValue[] {
    unformattedProperties = unformattedProperties || this.freePropertyModel.multiple;
    const multipleLines:string[] = unformattedProperties?.split('\n');
    const multipleLinesClean:string[] = _.reject(multipleLines, line => /^\s*$/.test(line));
    const freeProperties:INamedValue[] = [];
    _.each(multipleLinesClean, (line:string) => {
      const splitLine:string[] = line.split('\t');
      freeProperties.push({ name: splitLine[0].trim(), value: splitLine[1] });
    });
    return freeProperties;
  }

  private formatIndividualFreeProperty():INamedValue[] {
    this.freePropertyModel.freeProperties = this.freePropertyModel.freeProperties || [];
    if (this.freePropertyModel.name && this.freePropertyModel.value) {
      return [{
        name: this.freePropertyModel.name,
        value: this.freePropertyModel.value
      }];
    }
  }

  private getFreePropertiesDuplicateErrors(properties?:string):string[] {
    const formattedProperties = this.formatMultipleFreeProperties(properties);
    const propertiesFound:string[] = [];

    _.each(formattedProperties, (freeProperty:INamedValue, idx:number) => {
      // check if any of the new properties are already available in the model
      const propertyFoundInModel:boolean = _.some(this.model.freeProperties, (prop:INamedValue) => prop.name.toLowerCase() === freeProperty.name.toLowerCase());
      // check if there are duplicates in the list of new properties
      const propertiesFoundInList:INamedValue[] = _.filter(formattedProperties, (prop:INamedValue, propIdx:number) => idx > propIdx && prop.name.toLowerCase() === freeProperty.name.toLowerCase());

      if(propertyFoundInModel || propertiesFoundInList?.length){
        propertiesFound.push(`${freeProperty.name} (L${idx+1})`);
      }
    });

    return propertiesFound;
  }

  public addMultipleFreeProperties(event:Event):void {
    event?.preventDefault();
    this.globalProductEditService.multipleFreeProperties(!this.multiple);
    this.updateFreePropertyStatus();
  }

  // this method returns the list of fixed properties excluding the properties existing in the model
  private getFilteredFixedProperties():IPropertyGroup[] {
    const listProperties:IPropertyGroup[] = util.deepClone(this.globalProductService.properties());
    _.each(this.model.properties, (modelPropertyGroup:IPropertyGroup) => { // iterate over property groups
      const propListIdx:number = _.findIndex(listProperties, { name: modelPropertyGroup.name }); // find the index of the property from the model
      _.each(modelPropertyGroup.values, (modelProperty:IProperty) => {  // iterate over property values
        listProperties[propListIdx].values = _.reject(listProperties[propListIdx].values, (prop:IProperty) => prop.name === modelProperty.name);  // remove the property from the list
      });
    });
    return _.filter(listProperties, (propertyGroup:IPropertyGroup) => propertyGroup.values.length > 0); // remove all property groups without values from the list
  }

  private validateProperties(fields:IExtraFieldConfigurationObject[], model:IFixedPropertyModel):Promise<void|string[]> {
    return this.validateService.validateFields(fields, model, true);
  }

  private updateFreePropertyStatus():void {
    this.multiple = this.globalProductEditService.multipleFreeProperties();
    this.addFreePropertyButtonText = this.multiple ? 'Import Properties' : 'Add Free Property';
    this.excelText = this.multiple ? 'Add individual property' : 'From excel';
  }

  public closeDialog = ():void => {
    this.onClose.emit();
  };
}